import React, { useState, useMemo } from "react";
import EmptyChatMessageInput from "./EmptyChatMessageInput";
import { useUser } from "../../context/UserContext";
import { useHat } from "../../context/HatContext";
import { useWorkflow } from "../../context/WorkflowContext";
import { useModel } from "../../context/ModelContext";
import { Search, Building2, Network, ArrowLeft, Check, Info } from "lucide-react";
import { toast } from "sonner";

const EmptyChat = ({
  sendMessage,
  handleMic,
  isRecording,
  isSpeaking,
  focusMode,
  setFocusMode,

  // Quote props
  quotedText = null,
  sourceMessageId = null,
  clearQuote = () => { },
}: {
  sendMessage: (message: string, uploadedFiles?: File[], connector?: string, quotedText?: string, quotedMessageId?: string) => void;
  handleMic: () => void;
  isRecording: boolean;
  isSpeaking: boolean;
  focusMode: string;
  setFocusMode: (mode: string) => void;

  // Quote props
  quotedText?: string | null;
  sourceMessageId?: string | null;
  clearQuote?: () => void;
}) => {
  const { userDisplayName } = useUser();
  const { selectedModel } = useModel();
  const {
    hatsList = [],
    loading,
    selectedHat,
    setSelectedHat,
  } = useHat();
  const {
    workflows = [],
    selectedWorkflow,
    setSelectedWorkflow,
    fetchWorkflows,
  } = useWorkflow();
  const { userId } = useUser();

  // Fetch workflows on mount
  React.useEffect(() => {
    if (userId) {
      fetchWorkflows();
    }
  }, [userId, fetchWorkflows]);

  // Check if VA Directory should be disabled
  const isVADirectoryDisabled = useMemo(() => {
    if (!selectedModel) return true;

    const hasFileSupport = selectedModel.file_upload_support || false;
    const hasVASupport = selectedModel.tool_call_support || false;

    return !hasFileSupport || !hasVASupport;
  }, [selectedModel]);

  // state for Virtual Assistant Directory
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  // VA whose created/updated details the hover card is showing, and where.
  const [infoVA, setInfoVA] = useState<any | null>(null);
  const [infoPos, setInfoPos] = useState({ top: 0, left: 0 });

  // Group VAs by department
  const departmentGroups = useMemo(() => {
    if (!hatsList || hatsList.length === 0) return [];

    // Filter VAs based on search query
    let filteredVAs = hatsList;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredVAs = hatsList.filter(va =>
        va.hat_name?.toLowerCase().includes(query) ||
        va.summary?.toLowerCase().includes(query) ||
        va.department?.toLowerCase().includes(query)
      );
    }

    // Group by department - ONLY include VAs that have a valid department
    const groups = new Map<string, any[]>();

    filteredVAs.forEach(va => {
      // Skip VAs without a department or with empty department
      if (!va.department || va.department.trim() === '') {
        return; // Skip this VA
      }

      const department = va.department.trim();

      if (!groups.has(department)) {
        groups.set(department, []);
      }
      groups.get(department)!.push(va);
    });

    // Convert to array and sort
    const departmentArray = Array.from(groups.entries())
      .map(([departmentName, vas]) => ({
        departmentName,
        vas: vas.sort((a, b) => (a.hat_name || '').localeCompare(b.hat_name || '')),
        count: vas.length
      }))
      .sort((a, b) => {
        return a.departmentName.localeCompare(b.departmentName);
      });

    return departmentArray;
  }, [hatsList, searchQuery]);

  const hasVAsWithDepartments = useMemo(() => {
    return hatsList && hatsList.length > 0 && hatsList.some(va => va.department && va.department.trim() !== '');
  }, [hatsList]);

  // Toggle department selection — clicking same dept clears, clicking another switches
  const toggleDepartment = (departmentName: string) => {
    setSelectedDepartment(prev => (prev === departmentName ? null : departmentName));
  };

  // In VA view, scope search to the selected department so users see "no matches
  // in Finance" rather than silently swapping to whatever dept happens to match.
  const selectedGroup = useMemo(() => {
    if (!selectedDepartment || !hatsList) return null;
    const allVAsInDept = hatsList.filter(
      (va: any) => va.department && va.department.trim() === selectedDepartment
    );
    const query = searchQuery.trim().toLowerCase();
    const filteredVAs = query
      ? allVAsInDept.filter(
          (va: any) =>
            va.hat_name?.toLowerCase().includes(query) ||
            va.summary?.toLowerCase().includes(query)
        )
      : allVAsInDept;
    filteredVAs.sort((a: any, b: any) => (a.hat_name || '').localeCompare(b.hat_name || ''));
    return {
      departmentName: selectedDepartment,
      totalCount: allVAsInDept.length,
      vas: filteredVAs,
    };
  }, [selectedDepartment, hatsList, searchQuery]);

  // Get department color
  const getDepartmentColor = (departmentName: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-teal-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-cyan-500',
      'bg-emerald-500',
      'bg-rose-500',
    ];

    // Create a simple hash from department name
    let hash = 0;
    for (let i = 0; i < departmentName.length; i++) {
      hash = departmentName.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  // ISO timestamps out of DynamoDB; anything missing or unparseable reads
  // "Unknown" rather than "Invalid Date".
  const formatAuditDate = (value?: string) => {
    if (!value) return "Unknown";
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? "Unknown" : parsed.toLocaleString();
  };

  // The details panel opens up-and-left of the cursor so it clears the card
  // grid's right edge and the viewport bottom.
  const positionInfo = (e: React.MouseEvent) => {
    setInfoPos({ top: e.clientY + 14, left: e.clientX + 14 });
  };

  const handleHatClick = (event: React.MouseEvent, hat: any) => {
    event.preventDefault();
    if (selectedHat?.id === hat.id) {
      setSelectedHat(null);
    } else {
      setSelectedHat(hat);
      setSelectedWorkflow(null); // Clear workflow when VA is selected
    }
  };

  // function for VA click in directory
  const handleVAClick = (va: any) => {
    if (selectedHat?.id === va.id) {
      setSelectedHat(null);
      toast.info("Virtual Assistant deselected");
    } else {
      setSelectedHat(va);
      setSelectedWorkflow(null); // Clear workflow when VA is selected
      toast.success(`Selected: ${va.hat_name || 'Unnamed VA'}`);
    }
  };

  // function for Workflow click
  const handleWorkflowClick = (workflow: any) => {
    if (selectedWorkflow?.id === workflow.id) {
      setSelectedWorkflow(null);
      toast.info("Workflow deselected");
    } else {
      setSelectedWorkflow(workflow);
      setSelectedHat(null); // Clear VA when workflow is selected
      toast.success(`Selected Workflow: ${workflow.workflow_name || 'Unnamed Workflow'}`);
    }
  };

  // h-full, not h-screen: the parent in ChatLayout is already the viewport
  // minus the navbar and clips its overflow. A full-viewport child overflowed it
  // by the navbar's height, pushing the VA Directory's lower edge — and the
  // bottom of its scroll track — off-screen, so the scrollbar looked present but
  // unusable.
  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface text-primary" onClick={() => toast.dismiss()}>
      {/* Input zone — anchored near the top via pt so its position is stable
        * regardless of VA Directory height. (Previously this used flex-1 +
        * justify-center, which coupled input position to leftover space and made
        * the input shift whenever VA grew.) It never shrinks, so it is the fixed
        * slice of the viewport and the VA Directory below gets the rest. */}
      <div className="flex-shrink-0 w-full flex flex-col items-center px-4 pt-[20vh] sm:pt-[22vh] md:pt-[24vh] pb-6">
        <div className="w-full max-w-2xl flex flex-col space-y-6 md:space-y-8 lg:space-y-10">
          {/* Title Section */}
          <div className="w-full flex justify-center">
            <div className="w-full text-center">
              <h2 className="text-base sm:text-lg md:text-xl lg:text-[22px] font-medium tracking-tight text-primary">
                What can I do for you today?
              </h2>
            </div>
          </div>

          {/* Message Input Section */}
          <div className="w-full flex justify-center">
            <div className="w-full px-2 sm:px-0">
              <EmptyChatMessageInput
                sendMessage={sendMessage}
                handleMic={handleMic}
                isRecording={isRecording}
                isSpeaking={isSpeaking}
                focusMode={focusMode}
                setFocusMode={setFocusMode}
                quotedText={quotedText}
                sourceMessageId={sourceMessageId}
                clearQuote={clearQuote}
              />
            </div>
          </div>
        </div>
      </div>

      {/* VA Directory Section — takes the space the input zone leaves, via flex-1
        * rather than the spacer + min-h floor this used to carry. The floor made
        * the section taller than the leftover space on shorter windows, pushing
        * its lower edge — and the bottom of its scrollbar — past the fold; then
        * dropping the floor without growing the section left it hugging its
        * content at the very bottom, with the gap opening up above it instead.
        * Growing keeps its top edge just under the input, as on main, while the
        * inner scroll area absorbs anything that does not fit. */}
      {(loading || hasVAsWithDepartments) && (
        <div className="flex-1 w-full min-h-0 max-h-[57vh] sm:max-h-[59vh] md:max-h-[61vh] lg:max-h-[63vh] flex flex-col overflow-hidden border-t border-border-default/60">
          {/* Pinned header */}
          <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b border-border-default/60">
            <div className="w-full max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                {selectedGroup ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <button
                      onClick={() => {
                        setSelectedDepartment(null);
                        setSearchQuery("");
                      }}
                      className="flex items-center gap-1.5 px-2 py-1 -ml-2 rounded-md text-sm text-muted hover:text-primary hover:bg-surface-2 transition-colors flex-shrink-0"
                      aria-label="Back to departments"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">Departments</span>
                    </button>
                    <span className="text-subtle">/</span>
                    <div className={`w-6 h-6 rounded-md ${getDepartmentColor(selectedGroup.departmentName)} flex items-center justify-center flex-shrink-0`}>
                      <Building2 className="h-3 w-3 text-white" />
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-primary truncate">
                      {selectedGroup.departmentName}
                    </h3>
                    <span className="text-xs text-subtle flex-shrink-0">
                      · {selectedGroup.totalCount} assistant{selectedGroup.totalCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                ) : (
                  <h3 className="text-sm md:text-base font-semibold text-primary text-center sm:text-left">
                    Virtual Assistant Directory
                  </h3>
                )}

                {/* Search Input — placeholder switches to scope */}
                {!loading && hasVAsWithDepartments && (
                  <div className="relative w-full sm:max-w-xs">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className={`h-4 w-4 ${isVADirectoryDisabled ? 'text-subtle/60' : 'text-subtle'}`} />
                    </div>
                    <input
                      type="text"
                      className={`block w-full pl-9 pr-3 py-1.5 border rounded-md text-sm bg-surface-2 text-primary placeholder:text-subtle ${isVADirectoryDisabled
                        ? 'border-border-default text-subtle cursor-not-allowed'
                        : 'border-border-default focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                        }`}
                      placeholder={
                        selectedGroup
                          ? `Search in ${selectedGroup.departmentName}...`
                          : 'Search departments or VAs...'
                      }
                      value={searchQuery}
                      onChange={(e) => !isVADirectoryDisabled && setSearchQuery(e.target.value)}
                      disabled={isVADirectoryDisabled}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Scrollable content — the department/VA grid usually overflows,
            * so the scrollbar is kept permanently visible rather than appearing
            * only on hover, which left the cut-off row looking like the end of
            * the list. */}
          <div className="flex-1 min-h-0 overflow-x-hidden always-visible-scrollbar">
            <div className="w-full max-w-6xl mx-auto px-4 py-4">
              {/* Content Area */}
              <div className="w-full">
                {loading ? (
                  <div className="text-center py-10">
                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-muted mx-auto mb-3"></div>
                    <p className="text-sm text-subtle">Loading Virtual Assistant Directory...</p>
                  </div>
                ) : selectedGroup ? (
                  // VA view — replaces the dept grid in place
                  selectedGroup.vas.length === 0 ? (
                    <div className="text-center py-10 rounded-lg border border-border-default bg-surface-2">
                      <Building2 className="h-10 w-10 text-subtle/60 mx-auto mb-3" />
                      <p className="text-sm text-subtle">
                        {searchQuery.trim()
                          ? `No assistants in ${selectedGroup.departmentName} match your search`
                          : `No assistants in ${selectedGroup.departmentName} yet`}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
                      {selectedGroup.vas.map((va: any) => {
                        const isSelected = selectedHat?.id === va.id;
                        return (
                          <button
                            key={va.id}
                            onClick={() => !isVADirectoryDisabled && handleVAClick(va)}
                            className={`group flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all ${
                              isVADirectoryDisabled
                                ? 'cursor-not-allowed opacity-70 border-border-default bg-surface-2'
                                : isSelected
                                  ? 'border-border-strong bg-surface-3 ring-1 ring-border-strong shadow-sm'
                                  : 'border-border-default bg-surface-2 hover:border-border-strong hover:bg-surface-3'
                            }`}
                            disabled={isVADirectoryDisabled}
                          >
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-surface-overlay' : 'bg-surface-3 group-hover:bg-surface-2'}`}>
                              <img
                                src={isSelected ? '/va-icon.svg' : '/va-icon-bw.svg'}
                                alt=""
                                aria-hidden="true"
                                className="w-4 h-4"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm truncate ${isSelected ? 'text-violet-600 dark:!text-violet-400 font-semibold' : 'text-primary font-medium'}`}>
                                {va.hat_name || 'Unnamed VA'}
                              </div>
                              {va.summary && (
                                <p className="text-xs text-subtle line-clamp-2 mt-0.5">
                                  {va.summary}
                                </p>
                              )}
                            </div>
                            {/* A <span>, not a <button>: the card itself is a
                                <button> and nesting one inside is invalid HTML.
                                stopPropagation keeps a click on the icon from
                                selecting the VA. */}
                            <span
                              role="img"
                              aria-label={`Details for ${va.hat_name || 'Unnamed VA'}`}
                              className="flex-shrink-0 text-subtle hover:text-primary transition-colors"
                              onMouseEnter={(e) => { setInfoVA(va); positionInfo(e); }}
                              onMouseMove={positionInfo}
                              onMouseLeave={() => setInfoVA(null)}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Info className="w-3.5 h-3.5" />
                            </span>
                            {isSelected && (
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-600 dark:bg-violet-500 text-white flex items-center justify-center" aria-label="Selected">
                                <Check className="w-3 h-3" strokeWidth={3} />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : departmentGroups.length === 0 ? (
                  <div className="text-center py-10">
                    <Building2 className="h-10 w-10 text-subtle/60 mx-auto mb-3" />
                    <p className="text-sm text-subtle">
                      {searchQuery.trim() ? 'No Virtual Assistants match your search' : 'No Virtual Assistants available'}
                    </p>
                  </div>
                ) : (
                  // Department view — grid of department cards
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {departmentGroups.map((group) => (
                      <button
                        key={group.departmentName}
                        onClick={() => !isVADirectoryDisabled && toggleDepartment(group.departmentName)}
                        className={`w-full px-3 py-2.5 flex items-center gap-2.5 rounded-lg border text-left transition-all ${
                          isVADirectoryDisabled
                            ? 'cursor-not-allowed opacity-70 border-border-default bg-surface-2'
                            : 'border-border-default bg-surface-2 hover:bg-surface-3 hover:shadow-sm hover:border-border-strong'
                        }`}
                        disabled={isVADirectoryDisabled}
                      >
                        <div className={`w-8 h-8 rounded-md ${getDepartmentColor(group.departmentName)} flex items-center justify-center flex-shrink-0`}>
                          <Building2 className="h-4 w-4 text-white" />
                        </div>
                        <span className="flex-1 min-w-0 truncate text-sm font-medium text-primary">
                          {group.departmentName}
                        </span>
                        <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full bg-surface text-[11px] font-medium text-muted border border-border-default">
                          {group.count}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VA details hover card -- fixed so the directory's overflow-y-auto
          container can't clip it. */}
      {infoVA && (
        <div
          className="fixed z-50 rounded-md border border-border-default bg-surface-overlay px-3 py-2 text-xs shadow-lg pointer-events-none"
          style={{
            top: infoPos.top,
            left: infoPos.left,
            transform: 'translateX(calc(-100% - 28px))',
          }}
        >
          <div className="space-y-1 whitespace-nowrap">
            <div>
              <span className="text-subtle">Created:</span>{" "}
              <span className="text-primary">{formatAuditDate(infoVA.created_at)}</span>
            </div>
            <div>
              <span className="text-subtle">Created by:</span>{" "}
              <span className="text-primary">{infoVA.user_id || infoVA.userId || "Unknown"}</span>
            </div>
            <div>
              <span className="text-subtle">Last updated:</span>{" "}
              <span className="text-primary">{formatAuditDate(infoVA.updated_at)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmptyChat;